"use client";

import React, { useEffect, useRef } from "react";

// Adds click-and-drag horizontal scrolling (mouse) to a native overflow-x scroll container.
// Touch and trackpad keep the browser's own scrolling; only a real mouse triggers drag. This is
// the small bit of client JS that brings back Swiper's grab-and-drag without shipping Swiper (~33KB):
// the slides stay server-rendered, only these pointer handlers hydrate.
//
// Listeners are attached natively (addEventListener) rather than via React's onPointer* props: the
// slider is a dynamically-imported/hydrated subtree and React's synthetic pointer delegation did not
// reliably fire on it, whereas native listeners always catch the events.
//
// While dragging we suspend scroll-snap AND scroll-behavior (the page sets scroll-behavior:smooth
// globally, which would animate every per-move scrollLeft update and make the drag feel stuck), then
// restore both on release so the row re-snaps smoothly. A drag past a small threshold cancels the
// click it produces, so you don't navigate a card link when you meant to swipe.

// Attaches the drag-to-scroll behavior to an existing scroll container. Returns a cleanup function.
// Exported so SnapSlider (scroll-snap slider with vanilla controls) can share the exact behavior.
export function attachDragScroll(el: HTMLElement): () => void {
  let down = false;
  let startX = 0;
  let startLeft = 0;
  let moved = false;

  // Pointer capture is deliberately NOT taken here — it is taken in onPointerMove once the
  // gesture is actually a drag. Capturing on pointerdown retargets the click that follows to
  // `el`, and React dispatches onClick from a single root listener that only matches when the
  // event target sits inside that control's fiber. So capturing up front made every plain click
  // inside a slide land on the track instead of the control, silently dead — that is what broke
  // the /singular spec-card accordions (their buttons never saw the click at all).
  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") return; // let touch/trackpad scroll natively
    down = true;
    moved = false;
    startX = e.clientX;
    startLeft = el.scrollLeft;
    el.style.scrollSnapType = "none";
    el.style.scrollBehavior = "auto";
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!down) return;
    // Nothing is captured until the drag threshold, so a pointerup off `el` can be missed and
    // leave `down` stuck true — which would turn a later button-less hover into a phantom drag.
    if (e.buttons === 0) { end(e); return; }

    const dx = e.clientX - startX;
    if (!moved) {
      if (Math.abs(dx) <= 3) return; // still within click jitter — leave the click intact
      moved = true;
      // It is a real drag now: capture so it survives the cursor leaving the row. The click this
      // produces gets retargeted to `el`, where onClickCapture below swallows it.
      try { el.setPointerCapture(e.pointerId); } catch { /* pointer may not be capturable */ }
    }
    e.preventDefault(); // no text selection while dragging
    el.scrollLeft = startLeft - dx;
  };

  // The slides are full of <img>/<a>: pressing and moving on one starts the browser's native
  // HTML5 drag (ghost image) which fires pointercancel and kills the scroll-drag after a few
  // pixels. Swiper suppressed this the same way; without it mouse drag "doesn't work".
  const onNativeDragStart = (e: Event) => e.preventDefault();

  // Vertical wheel/trackpad over the row must scroll the PAGE, exactly like the live Swiper
  // (which was not a scroll container, so the wheel always reached the page). Browsers normally
  // chain the wheel out of a horizontal-only scroller, but some input paths latch it to the row
  // and swallow it — so chain explicitly. Horizontal deltas keep scrolling the row natively.
  const onWheel = (e: WheelEvent) => {
    if (e.ctrlKey || e.defaultPrevented) return; // pinch-zoom / already handled
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // horizontal intent -> native row scroll
    const unit = e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? window.innerHeight : 1;
    e.preventDefault();
    window.scrollBy({ top: e.deltaY * unit, left: 0, behavior: "instant" as ScrollBehavior });
  };

  const end = (e: PointerEvent) => {
    if (!down) return;
    down = false;
    try { el.releasePointerCapture(e.pointerId); } catch { /* nothing to release */ }
    el.style.scrollSnapType = "";
    el.style.scrollBehavior = "";
    el.style.cursor = "grab"; // back to the resting grab affordance (not "" / default)
    el.style.userSelect = "";
  };

  // Swallow the click that ends a drag so it doesn't activate a card link / button.
  const onClickCapture = (e: MouseEvent) => {
    if (moved) {
      e.preventDefault();
      e.stopPropagation();
      moved = false;
    }
  };

  el.addEventListener("pointerdown", onPointerDown);
  el.addEventListener("pointermove", onPointerMove);
  el.addEventListener("pointerup", end);
  el.addEventListener("pointercancel", end);
  el.addEventListener("click", onClickCapture, true);
  el.addEventListener("dragstart", onNativeDragStart);
  el.addEventListener("wheel", onWheel, { passive: false });

  return () => {
    el.removeEventListener("pointerdown", onPointerDown);
    el.removeEventListener("pointermove", onPointerMove);
    el.removeEventListener("pointerup", end);
    el.removeEventListener("pointercancel", end);
    el.removeEventListener("click", onClickCapture, true);
    el.removeEventListener("dragstart", onNativeDragStart);
    el.removeEventListener("wheel", onWheel);
  };
}

interface DragScrollProps {
  className?: string;
  children: React.ReactNode;
}

export default function DragScroll({ className = "", children }: DragScrollProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return attachDragScroll(el);
  }, []);

  return (
    <div ref={ref} className={className} style={{ WebkitOverflowScrolling: "touch", cursor: "grab" }}>
      {children}
    </div>
  );
}
